import styled from '@emotion/styled';

import { ERROR_TYPE, ErrorPage } from '@innovamat/ga-features';
import { Drawer } from '@innovamat/glimmer-components';

import { TEST_TYPE } from '../../../../modules/test/test';
import { TestAndResultsDrawerHeader } from './components/drawer-header';
import { useTestAndResults } from './context/test-and-results-provider';
import { MockExamsDrawerContent } from './mock-exams-drawer';
import { MockExamsDrawerSkeleton } from './mock-exams-drawer/components/mock-exams-drawer-skeleton';
import { TestsDrawerContent } from './test-drawer';
import { TestDrawerSkeleton } from './test-drawer/components/test-drawer-skeleton';
import { useTestAndResultsDrawer } from './use-test-and-result-drawer';

const Container = styled.div`
  padding: 16px 32px;
  width: 100%;
`;

function TestAndResultsDrawer(): JSX.Element {
  const { drawerId, rowSelected } = useTestAndResults();
  const {
    detailData,
    formattedDate,
    formattedParticipation,
    isError,
    isLoading,
    lastSelectedType,
    handleDrawerClose,
  } = useTestAndResultsDrawer();

  const drawerContent = [
    {
      type: TEST_TYPE.MOCK,
      content: (
        <MockExamsDrawerContent
          detailData={detailData}
          date={formattedDate}
          participation={formattedParticipation}
        />
      ),
      skeleton: <MockExamsDrawerSkeleton />,
      width: '90%',
    },
    {
      type: TEST_TYPE.CONMAT,
      content: (
        <TestsDrawerContent
          detailData={detailData}
          date={formattedDate}
          participation={formattedParticipation}
        />
      ),
      skeleton: <TestDrawerSkeleton />,
      width: '700px',
    },
    {
      type: TEST_TYPE.COSMOS,
      content: (
        <TestsDrawerContent
          detailData={detailData}
          date={formattedDate}
          participation={formattedParticipation}
        />
      ),
      skeleton: <TestDrawerSkeleton />,
      width: '700px',
    },
  ];

  const renderContent = (): JSX.Element => {
    if (!rowSelected) {
      return (
        drawerContent.find((content) => content.type === lastSelectedType)
          ?.skeleton ?? <MockExamsDrawerSkeleton />
      );
    }

    if (isLoading) {
      return (
        drawerContent.find((content) => content.type === rowSelected.type)
          ?.skeleton ?? <MockExamsDrawerSkeleton />
      );
    }
    if (isError) {
      return <ErrorPage errorType={ERROR_TYPE.NOT_AVAILABLE} />;
    }

    const content = drawerContent.find(
      (content) => content.type === rowSelected.type
    );

    return (
      content?.content ?? <ErrorPage errorType={ERROR_TYPE.NOT_AVAILABLE} />
    );
  };

  const drawerWidth = drawerContent.find(
    (content) => content.type === (rowSelected?.type ?? lastSelectedType)
  )?.width;

  return (
    <Drawer
      key="mock-exams-drawer"
      open={Boolean(drawerId)}
      onClose={handleDrawerClose}
      roundedBorder
      closeOnClickOutside
      customStyles={{
        width: drawerWidth,
      }}
    >
      <Container>
        <TestAndResultsDrawerHeader />
        {renderContent()}
      </Container>
    </Drawer>
  );
}

export { TestAndResultsDrawer };
